import React from 'react';
import {Button, Space, Table, Tag, Tooltip} from 'antd';
import type {TableColumnsType} from 'antd';
import {useTranslation} from 'react-i18next';
import {formatEpochToDateTime} from '../../../../utils/dateTimeUtils';
import {ComplaintStatus, CustomerComplaint} from '../graphql/types/CustomerComplaintType';
import {resolveComplaintSourceI18nKey} from '../utils/complaintSource';

interface CustomerComplaintTableProps {
    data: CustomerComplaint[];
    loading: boolean;
    onOpenDetail: (record: CustomerComplaint) => void;
    /**
     * 主动认责回调（PENDING_DSP_ACTION 状态）；调用 acknowledgeCustomerComplaint。
     * undefined 时该行不展示"主动认责"按钮（仅查看视角 / 已完结 Tab）。
     */
    onAcknowledge?: (record: CustomerComplaint) => void;
    /**
     * 放弃申诉回调（APPEALING 状态）；调用 abandonCustomerComplaintAppeal（含级联 cancel review_task）。
     * undefined 时该行不展示"放弃申诉"按钮（仅查看视角 / 已完结 Tab）。
     */
    onAbandon?: (record: CustomerComplaint) => void;
    /**
     * 是否展示 DSP 列。DSP 视角下所有行都属于当前 DSP，展示自己的名字是冗余信息，
     * 故仅 wms / HQ（可跨 DSP 查看）展示。
     */
    showDsp?: boolean;
}

const STATUS_COLOR: Record<string, string> = {
    [ComplaintStatus.PENDING_DSP_ACTION]: 'orange',
    [ComplaintStatus.APPEALING]: 'blue',
    [ComplaintStatus.REVOKED]: 'green',
    [ComplaintStatus.ENFORCED]: 'red',
    [ComplaintStatus.ACKNOWLEDGED]: 'volcano',
    [ComplaintStatus.TIMEOUT]: 'magenta',
    [ComplaintStatus.CLOSED]: 'default',
};

const URGENT_HOURS = 6;

/** 距 deadline 的小时数；为空或解析失败返回 null。入参为 epoch seconds */
const hoursUntilDeadline = (deadline?: number): number | null => {
    if (deadline == null) return null;
    const ms = deadline * 1000 - Date.now();
    if (Number.isNaN(ms)) return null;
    return ms / 3600_000;
};

const CustomerComplaintTable: React.FC<CustomerComplaintTableProps> = ({
    data,
    loading,
    onOpenDetail,
    onAcknowledge,
    onAbandon,
    showDsp = false,
}) => {
    const {t} = useTranslation();

    /** 待处理 tab 下、剩余 < 6h（含已超时）视为即将超时/超时，时效列飘红 */
    const isUrgent = (record: CustomerComplaint): boolean => {
        if (record.status !== ComplaintStatus.PENDING_DSP_ACTION) return false;
        const hrs = hoursUntilDeadline(record.appealDeadlineAt);
        return hrs !== null && hrs < URGENT_HOURS;
    };

    /** 待处理 tab 下、已过截止（定时任务尚未流转为 TIMEOUT 的窗口期） */
    const isOverdue = (record: CustomerComplaint): boolean => {
        if (record.status !== ComplaintStatus.PENDING_DSP_ACTION) return false;
        const hrs = hoursUntilDeadline(record.appealDeadlineAt);
        return hrs !== null && hrs < 0;
    };

    /** 时效列：展示截止时间（与 PRD 截图一致）；isUrgent 时文案变红 */
    const renderDeadlineCountdown = (record: CustomerComplaint) => {
        const text = formatEpochToDateTime(record.appealDeadlineAt);
        if (!text || text === '-') return '-';
        if (isUrgent(record)) {
            return <span style={{color: '#ff4d4f'}}>{text}</span>;
        }
        return text;
    };

    const columns: TableColumnsType<CustomerComplaint> = [
        {
            title: t('customerComplaint.table.trackingNumber'),
            dataIndex: 'trackingNumber',
            key: 'trackingNumber',
            ellipsis: true,
            render: (v: string | undefined, record: CustomerComplaint) => (
                <div className="flex items-center justify-between gap-2">
                    <span>{v ?? '-'}</span>
                    {isUrgent(record) && (
                        <Tag color="red" style={{marginRight: 0}}>
                            {isOverdue(record)
                                ? t('customerComplaint.table.deadlineOverdueTag')
                                : t('customerComplaint.table.deadlineUrgentTag')}
                        </Tag>
                    )}
                </div>
            ),
        },
        {
            title: t('customerComplaint.table.createdAt'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            ellipsis: true,
            render: formatEpochToDateTime,
        },
        {
            title: t('customerComplaint.table.type'),
            dataIndex: 'complaintType',
            key: 'complaintType',
            ellipsis: true,
            render: (v: string) => t(`customerComplaint.type.${v}`, v),
        },
        {
            title: t('customerComplaint.table.status'),
            dataIndex: 'status',
            key: 'status',
            ellipsis: true,
            render: (v: string) => (
                <Tag color={STATUS_COLOR[v] ?? 'default'}>
                    {t(`customerComplaint.status.${v}`)}
                </Tag>
            ),
        },
        {
            title: t('customerComplaint.table.deadlineCountdown'),
            key: 'deadlineCountdown',
            ellipsis: true,
            render: (_: unknown, record: CustomerComplaint) => renderDeadlineCountdown(record),
        },
        {
            title: t('customerComplaint.table.warehouse'),
            key: 'warehouse',
            ellipsis: true,
            render: (_: unknown, record: CustomerComplaint) =>
                record.warehouseName || record.warehouseId || '-',
        },
        ...(showDsp
            ? [{
                title: t('customerComplaint.table.dsp'),
                key: 'dsp',
                ellipsis: true,
                render: (_: unknown, record: CustomerComplaint) => record.dspName || record.dspId || '-',
            }]
            : []),
        {
            title: t('customerComplaint.table.thirdSortingCode'),
            dataIndex: 'thirdSortingCode',
            key: 'thirdSortingCode',
            ellipsis: true,
            width: 100,
            render: (text: string | null) => text || '-',
        },
        {
            title: t('customerComplaint.table.driverId'),
            key: 'driver',
            ellipsis: true,
            render: (_: unknown, record: CustomerComplaint) =>
                record.driverName || record.driverId || '-',
        },
        {
            // 供质控/DSP 核验「是否投递到正确地址」；地址较长故 ellipsis + Tooltip 展开。
            title: t('customerComplaint.table.recipientAddress'),
            key: 'recipientAddress',
            ellipsis: true,
            width: 220,
            render: (_: unknown, record: CustomerComplaint) => {
                const addr = record.recipientAddress;
                return addr ? (
                    <Tooltip title={addr} placement="topLeft">
                        <span>{addr}</span>
                    </Tooltip>
                ) : (
                    '-'
                );
            },
        },
        {
            title: t('customerComplaint.table.orderOverallStatus'),
            dataIndex: 'orderOverallStatus',
            key: 'orderOverallStatus',
            ellipsis: true,
            // 仅展示，不提供筛选；order 已删除或查询失败时为 null，显示 '-'
            render: (v?: string) =>
                v ? (
                    <Tag color="processing">
                        {t(`enum.overallStatus.${v}`, {defaultValue: v}) as string}
                    </Tag>
                ) : (
                    '-'
                ),
        },
        {
            title: t('customerComplaint.table.source'),
            dataIndex: 'source',
            key: 'source',
            ellipsis: true,
            // 与筛选项保持一致：EMAIL/PHONE→客服，OTHER→系统；未识别 source 兜底显示原值
            render: (v?: string) => {
                const key = resolveComplaintSourceI18nKey(v);
                if (!key) return '-';
                // i18n 缺该 key 时回落到原始 source code，避免展示空白
                return t(key, {defaultValue: v ?? ''}) as string;
            },
        },
        {
            title: t('customerComplaint.table.appealSubmittedAt'),
            dataIndex: 'appealSubmittedAt',
            key: 'appealSubmittedAt',
            ellipsis: true,
            render: formatEpochToDateTime,
        },
        {
            title: t('customerComplaint.table.action'),
            key: 'action',
            fixed: 'right',
            // wms / HQ 仅查看视角（回调均未传）只有单个"查看"按钮，列宽收窄
            width: onAbandon || onAcknowledge ? 160 : 80,
            render: (_: unknown, record: CustomerComplaint) => {
                // "放弃申诉" 按钮文案对用户保持一致，仅根据状态切换实际调用的接口：
                //   PENDING_DSP_ACTION → onAcknowledge（acknowledgeCustomerComplaint，无 review_task 可取消）
                //   APPEALING          → onAbandon    （abandonCustomerComplaintAppeal，级联 cancel review_task）
                // 确认弹窗由 Panel 层 Modal.confirm 统一弹出（与详情抽屉复用同一范式）
                const abandonBtn = (onClick: () => void) => (
                    <Button type="link" size="small" danger onClick={onClick}>
                        {t('customerComplaint.table.abandonAppeal')}
                    </Button>
                );
                // PENDING：我要申诉 + 放弃申诉（背后调 acknowledge）
                if (record.status === ComplaintStatus.PENDING_DSP_ACTION && onAcknowledge) {
                    return (
                        <Space size={4}>
                            <Button type="link" size="small" onClick={() => onOpenDetail(record)}>
                                {t('customerComplaint.table.submitAppeal')}
                            </Button>
                            {abandonBtn(() => onAcknowledge(record))}
                        </Space>
                    );
                }
                // APPEALING：查看 + 放弃申诉（背后调 abandon）
                if (record.status === ComplaintStatus.APPEALING && onAbandon) {
                    return (
                        <Space size={4}>
                            <Button type="link" size="small" onClick={() => onOpenDetail(record)}>
                                {t('customerComplaint.table.view')}
                            </Button>
                            {abandonBtn(() => onAbandon(record))}
                        </Space>
                    );
                }
                // 终态 / 仅查看视角：只展示查看
                return (
                    <Button type="link" size="small" onClick={() => onOpenDetail(record)}>
                        {t('customerComplaint.table.view')}
                    </Button>
                );
            },
        },
    ];

    return (
        <>
            {/* 紧急行底色：用 !important 同时覆盖默认与 hover 态，避免 antd hover 灰色穿透 */}
            <style>{`
                .customer-complaint-urgent-row td {
                    background-color: #fef2f2 !important;
                }
                .customer-complaint-urgent-row:hover td {
                    background-color: #fee2e2 !important;
                }
            `}</style>
            <Table
                rowKey="id"
                columns={columns}
                dataSource={data}
                loading={loading}
                pagination={false}
                scroll={{x: 'max-content'}}
                size="middle"
                tableLayout="auto"
                rowClassName={(record) => (isUrgent(record) ? 'customer-complaint-urgent-row' : '')}
            />
        </>
    );
};

export default CustomerComplaintTable;
